import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { issuesAPI, interviewAPI, codeRepoAPI, BASE_URL } from '../services/api';
import axios from 'axios';
import {
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  Chat as ChatIcon,
  MailOutline as MailOutlineIcon,
  BugReport as BugReportIcon,
  Lightbulb as LightbulbIcon,
  Help as HelpIcon,
  Message as MessageIcon,
  ArrowForward as ArrowForwardIcon,
  Mic as MicIcon,
  Assignment as AssignmentIcon,
  Code as CodeIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';

function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [pendingIssues, setPendingIssues] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [pendingFeedback, setPendingFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [recentInterviews, setRecentInterviews] = useState([]);
  const [recentCodeRepos, setRecentCodeRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Verify user is admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/user');
    }
  }, [user, navigate, showToast]);

  useEffect(() => {
    fetchPendingIssues();
    fetchStatistics();
    fetchPendingFeedback();
    fetchFeedbackStats();
    fetchRecentInterviews();
    fetchRecentCodeRepos();
  }, []);

  const fetchPendingIssues = async () => {
    try {
      const response = await issuesAPI.getAllIssues({ status: 'pending' });
      setPendingIssues(response.data.issues || []);
    } catch (error) {
      console.error('Error fetching pending issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await issuesAPI.getStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchPendingFeedback = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/feedback/admin/all?status=pending`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setPendingFeedback(response.data || []);
    } catch (error) {
      console.error('Error fetching pending feedback:', error);
    }
  };

  const fetchFeedbackStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/feedback/admin/statistics`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setFeedbackStats(response.data);
    } catch (error) {
      console.error('Error fetching feedback statistics:', error);
    }
  };

  const fetchRecentInterviews = async () => {
    try {
      const response = await interviewAPI.getRecentCompleted(5);
      setRecentInterviews(response.data.completions || []);
    } catch (error) {
      console.error('Error fetching recent interviews:', error);
    }
  };

  const fetchRecentCodeRepos = async () => {
    try {
      const response = await codeRepoAPI.getRecentCompleted(5);
      setRecentCodeRepos(response.data.completions || []);
    } catch (error) {
      console.error('Error fetching recent code repos:', error);
    }
  };

  const getSourceTypeLabel = (type) => {
    const labels = {
      quiz: 'Quiz',
      assignment: 'Assignment',
      assetQuiz: 'Asset Quiz',
      aiQuestion: 'AI Question',
      flashcard: 'Flashcard'
    };
    return labels[type] || type;
  };

  const getIssueTypeLabel = (type) => {
    const labels = {
      wrong_answer: 'Wrong Answer',
      ambiguous_question: 'Ambiguous Question',
      typo: 'Typo/Grammar',
      missing_option: 'Missing Option',
      other: 'Other'
    };
    return labels[type] || type;
  };

  return (
    <div className="container">
      {/* Welcome Card */}
      <div className="card">
        <div style={{ textAlign: 'center', padding: '60px 24px' }}>
          <h2 style={{ color: '#667eea', fontSize: '28px', marginBottom: '12px' }}>
            Welcome to Admin Dashboard
          </h2>
          <p style={{ color: '#718096', fontSize: '16px', lineHeight: '1.6' }}>
            Use the navigation menu to manage quizzes, students, groups, and more.
          </p>
        </div>
      </div>

      {/* Cards Grid Container */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '24px',
        marginTop: '24px'
      }}>
        {/* Flagged Questions Section */}
        <div className="card" style={{ margin: 0 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '22px', 
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FlagIcon sx={{ fontSize: 28, color: '#e53e3e' }} /> Pending Question Issues
            {statistics && statistics.byStatus && (
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                padding: '4px 12px',
                backgroundColor: '#fed7d7',
                color: '#c53030',
                borderRadius: '12px'
              }}>
                {statistics.byStatus.pending || 0}
              </span>
            )}
          </h3>
          <button
            onClick={() => navigate('/admin/issues')}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#667eea',
              backgroundColor: '#edf2f7',
              border: '1px solid #cbd5e0',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#e2e8f0';
              e.target.style.borderColor = '#a0aec0';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#edf2f7';
              e.target.style.borderColor = '#cbd5e0';
            }}
          >
            View All Issues <ArrowForwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            Loading pending issues...
          </div>
        ) : pendingIssues.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            <CheckCircleIcon sx={{ fontSize: 48, marginBottom: '12px', color: '#48bb78' }} />
            <div style={{ fontSize: '16px' }}>No pending issues! All questions are in good shape.</div>
          </div>
        ) : (
          <div>
            {pendingIssues.slice(0, 5).map((issue) => (
              <div
                key={issue._id}
                style={{
                  padding: '16px',
                  marginBottom: '12px',
                  backgroundColor: '#fef5e7',
                  border: '1px solid #f9e79f',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => navigate('/admin/issues', { state: { selectedIssueId: issue._id } })}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#fcf3cf';
                  e.currentTarget.style.borderColor = '#f7dc6f';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#fef5e7';
                  e.currentTarget.style.borderColor = '#f9e79f';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '12px',
                        padding: '3px 10px',
                        backgroundColor: '#667eea',
                        color: '#fff',
                        borderRadius: '12px',
                        fontWeight: '600'
                      }}>
                        {getSourceTypeLabel(issue.sourceType)}
                      </span>
                      <span style={{
                        fontSize: '12px',
                        padding: '3px 10px',
                        backgroundColor: '#ed8936',
                        color: '#fff',
                        borderRadius: '12px',
                        fontWeight: '600'
                      }}>
                        {getIssueTypeLabel(issue.issueType)}
                      </span>
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#2d3748',
                      marginBottom: '8px',
                      fontWeight: '600'
                    }}>
                      {issue.questionSnapshot?.question?.substring(0, 100)}
                      {issue.questionSnapshot?.question?.length > 100 && '...'}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#4a5568',
                      marginBottom: '8px'
                    }}>
                      <strong>Issue:</strong> {issue.description.substring(0, 150)}
                      {issue.description.length > 150 && '...'}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#718096'
                    }}>
                      Reported by: {issue.reportedBy?.name || 'Unknown'} • {new Date(issue.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{
                    marginLeft: '12px'
                  }}>
                    <ArrowForwardIcon sx={{ fontSize: 20, color: '#718096' }} />
                  </div>
                </div>
              </div>
            ))}
            {pendingIssues.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <button
                  onClick={() => navigate('/admin/issues')}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#667eea',
                    backgroundColor: 'transparent',
                    border: '2px solid #667eea',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#667eea';
                    e.target.style.color = '#fff';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#667eea';
                  }}
                >
                  View {pendingIssues.length - 5} More Pending Issues
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Feedback Section */}
      <div className="card" style={{ margin: 0 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '22px', 
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <ChatIcon sx={{ fontSize: 28, color: '#3182ce' }} /> User Feedback
            {feedbackStats && feedbackStats.byStatus && (
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                padding: '4px 12px',
                backgroundColor: '#fed7d7',
                color: '#c53030',
                borderRadius: '12px'
              }}>
                {feedbackStats.byStatus.pending || 0}
              </span>
            )}
          </h3>
          <button
            onClick={() => navigate('/admin/feedback')}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#667eea',
              backgroundColor: '#edf2f7',
              border: '1px solid #cbd5e0',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#e2e8f0';
              e.target.style.borderColor = '#a0aec0';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#edf2f7';
              e.target.style.borderColor = '#cbd5e0';
            }}
          >
            View All Feedback <ArrowForwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            Loading pending feedback...
          </div>
        ) : pendingFeedback.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            <MailOutlineIcon sx={{ fontSize: 48, marginBottom: '12px', color: '#718096' }} />
            <div style={{ fontSize: '16px' }}>No pending feedback! All caught up.</div>
          </div>
        ) : (
          <div>
            {pendingFeedback.slice(0, 5).map((feedback) => {
              const TypeIcon = ({ type }) => {
                const icons = {
                  bug: <BugReportIcon sx={{ fontSize: 14 }} />,
                  feature_request: <LightbulbIcon sx={{ fontSize: 14 }} />,
                  question_suggestion: <HelpIcon sx={{ fontSize: 14 }} />,
                  general_message: <MessageIcon sx={{ fontSize: 14 }} />
                };
                return icons[type] || null;
              };
              const typeLabels = {
                bug: 'Bug Report',
                feature_request: 'Feature Request',
                question_suggestion: 'Question Suggestion',
                general_message: 'General Message'
              };
              const typeColors = {
                bug: '#e53e3e',
                feature_request: '#805ad5',
                question_suggestion: '#3182ce',
                general_message: '#38a169'
              };
              
              return (
                <div
                  key={feedback._id}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    backgroundColor: '#ebf8ff',
                    border: '1px solid #90cdf4',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => navigate('/admin/feedback', { state: { selectedFeedbackId: feedback._id } })}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#bee3f8';
                    e.currentTarget.style.borderColor = '#4299e1';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#ebf8ff';
                    e.currentTarget.style.borderColor = '#90cdf4';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '12px',
                          padding: '3px 10px',
                          backgroundColor: typeColors[feedback.type],
                          color: '#fff',
                          borderRadius: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <TypeIcon type={feedback.type} />
                          <span>{typeLabels[feedback.type]}</span>
                        </span>
                      </div>
                      <div style={{
                        fontSize: '15px',
                        color: '#2d3748',
                        marginBottom: '8px',
                        fontWeight: '600'
                      }}>
                        {feedback.title}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#4a5568',
                        marginBottom: '8px'
                      }}>
                        {feedback.description.substring(0, 150)}
                        {feedback.description.length > 150 && '...'}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#718096'
                      }}>
                        Submitted by: {feedback.user?.name || 'Unknown'} • {new Date(feedback.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '20px',
                      marginLeft: '12px'
                    }}>
                      →
                    </div>
                  </div>
                </div>
              );
            })}
            {pendingFeedback.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <button
                  onClick={() => navigate('/admin/feedback')}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#667eea',
                    backgroundColor: 'transparent',
                    border: '2px solid #667eea',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#667eea';
                    e.target.style.color = '#fff';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#667eea';
                  }}
                >
                  View {pendingFeedback.length - 5} More Pending Feedback
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent Completed Interviews Section */}
      <div className="card" style={{ margin: 0 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '22px', 
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <MicIcon sx={{ fontSize: 28, color: '#38a169' }} /> Recent Completed Interviews
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              padding: '4px 12px',
              backgroundColor: '#c6f6d5',
              color: '#22543d',
              borderRadius: '12px'
            }}>
              {recentInterviews.length}
            </span>
          </h3>
          <button
            onClick={() => navigate('/admin/interviews')}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#667eea',
              backgroundColor: '#edf2f7',
              border: '1px solid #cbd5e0',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#e2e8f0';
              e.target.style.borderColor = '#a0aec0';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#edf2f7';
              e.target.style.borderColor = '#cbd5e0';
            }}
          >
            View All Interviews <ArrowForwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            Loading recent interviews...
          </div>
        ) : recentInterviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            <AssignmentIcon sx={{ fontSize: 48, marginBottom: '12px', color: '#718096' }} />
            <div style={{ fontSize: '16px' }}>No completed interviews yet.</div>
          </div>
        ) : (
          <div>
            {recentInterviews.map((interview) => {
              const difficultyColors = {
                beginner: '#48bb78',
                intermediate: '#ed8936',
                expert: '#e53e3e'
              };

              return (
                <div
                  key={interview._id}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    backgroundColor: '#f0fff4',
                    border: '1px solid #9ae6b4',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => navigate(`/admin/interview/${interview.interviewId}/responses`, { 
                    state: { responseId: interview._id, studentId: interview.studentId } 
                  })}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#c6f6d5';
                    e.currentTarget.style.borderColor = '#68d391';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0fff4';
                    e.currentTarget.style.borderColor = '#9ae6b4';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{
                          fontSize: '12px',
                          padding: '3px 10px',
                          backgroundColor: difficultyColors[interview.difficulty] || '#718096',
                          color: '#fff',
                          borderRadius: '12px',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}>
                          {interview.difficulty}
                        </span>
                        {interview.overallScore !== null && (
                          <span style={{
                            fontSize: '12px',
                            padding: '3px 10px',
                            backgroundColor: interview.overallScore >= 70 ? '#48bb78' : interview.overallScore >= 50 ? '#ed8936' : '#e53e3e',
                            color: '#fff',
                            borderRadius: '12px',
                            fontWeight: '600'
                          }}>
                            Score: {interview.overallScore}/100
                          </span>
                        )}
                        {interview.attemptNumber > 1 && (
                          <span style={{
                            fontSize: '12px',
                            padding: '3px 10px',
                            backgroundColor: '#805ad5',
                            color: '#fff',
                            borderRadius: '12px',
                            fontWeight: '600'
                          }}>
                            Attempt #{interview.attemptNumber}
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: '15px',
                        color: '#2d3748',
                        marginBottom: '6px',
                        fontWeight: '600'
                      }}>
                        {interview.interviewTitle}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#2d3748',
                        marginBottom: '8px'
                      }}>
                        <strong>Student:</strong> {interview.studentName}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#718096'
                      }}>
                        Completed: {new Date(interview.submittedAt).toLocaleString()}
                      </div>
                    </div>
                    <div style={{
                      marginLeft: '12px'
                    }}>
                      <ArrowForwardIcon sx={{ fontSize: 20, color: '#718096' }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Completed Code Repos Section */}
      <div className="card" style={{ margin: 0 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '22px', 
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <CodeIcon sx={{ fontSize: 28, color: '#805ad5' }} /> Recent Completed Code Repos
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              padding: '4px 12px',
              backgroundColor: '#e9d8fd',
              color: '#44337a',
              borderRadius: '12px'
            }}>
              {recentCodeRepos.length}
            </span>
          </h3>
          <button
            onClick={() => navigate('/admin/code-repos')}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#667eea',
              backgroundColor: '#edf2f7',
              border: '1px solid #cbd5e0',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#e2e8f0';
              e.target.style.borderColor = '#a0aec0';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#edf2f7';
              e.target.style.borderColor = '#cbd5e0';
            }}
          >
            View All Code Repos <ArrowForwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            Loading recent code repos...
          </div>
        ) : recentCodeRepos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            <InventoryIcon sx={{ fontSize: 48, marginBottom: '12px', color: '#718096' }} />
            <div style={{ fontSize: '16px' }}>No completed code repos yet.</div>
          </div>
        ) : (
          <div>
            {recentCodeRepos.map((repo) => {
              const levelColors = {
                beginner: '#48bb78',
                intermediate: '#ed8936',
                advanced: '#e53e3e',
                expert: '#805ad5'
              };

              return (
                <div
                  key={repo._id}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    backgroundColor: '#faf5ff',
                    border: '1px solid #d6bcfa',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => navigate(`/admin/code-repos/${repo.repoId}`)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#e9d8fd';
                    e.currentTarget.style.borderColor = '#b794f4';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#faf5ff';
                    e.currentTarget.style.borderColor = '#d6bcfa';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{
                          fontSize: '12px',
                          padding: '3px 10px',
                          backgroundColor: levelColors[repo.level] || '#718096',
                          color: '#fff',
                          borderRadius: '12px',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}>
                          {repo.level}
                        </span>
                        {repo.score !== null && repo.score !== undefined && (
                          <span style={{
                            fontSize: '12px',
                            padding: '3px 10px',
                            backgroundColor: repo.score >= 80 ? '#48bb78' : repo.score >= 60 ? '#ed8936' : '#e53e3e',
                            color: '#fff',
                            borderRadius: '12px',
                            fontWeight: '600'
                          }}>
                            Score: {repo.score}%
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: '15px',
                        color: '#2d3748',
                        marginBottom: '6px',
                        fontWeight: '600'
                      }}>
                        {repo.assignmentTitle}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#4a5568',
                        marginBottom: '8px'
                      }}>
                        <strong>Repo:</strong> {repo.repoTitle}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#2d3748',
                        marginBottom: '8px'
                      }}>
                        <strong>Student:</strong> {repo.studentName}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#718096'
                      }}>
                        Completed: {new Date(repo.completedAt).toLocaleString()}
                      </div>
                    </div>
                    <div style={{
                      marginLeft: '12px'
                    }}>
                      <ArrowForwardIcon sx={{ fontSize: 20, color: '#718096' }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
